import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box, Stack, Typography, Button, IconButton, TextField, MenuItem,
  Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup,
  Tooltip as MuiTooltip, Alert, AlertTitle, CircularProgress, Chip,
  Divider, Tabs, Tab, Link as MuiLink, Paper,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableRow,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  DEFAULT_GEHALT, STEUERKLASSEN, BUNDESLAENDER, AVAILABLE_YEARS, MONATE,
  calcGehaltResult, calcNettoComparison, calcAgZuschuss, fmtEuro, getTaxConfig,
} from '../utils/salaryCalculations';
import { useSalarySettings } from '../hooks/useSalarySettings';
import { useModules, calculateAge } from '../context/ModuleContext';
import { fetchBmfTaxValidation } from '../lib/bmfValidator';
import { PageHeader, SectionCard, CurrencyField } from '../components/mui';
import SalaryHistoryTab from './SalaryHistoryTab';
import SalaryForecastTab from './SalaryForecastTab';

// ─── Lohnsteuer Tooltip Body ──────────────────────────────────────────────────
// Formel der Tarifzone aus der Config des gewählten Jahres (§ 32a EStG)
function tarifZoneText(zve, cfg) {
  const n = (v, dec = 0) => v.toLocaleString('de-DE', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const pct = (v) => n(v * 100, v * 100 % 1 ? 1 : 0) + ' %';
  if (zve <= cfg.gfb) return `Zone 1: ZVE ≤ ${n(cfg.gfb)} (Grundfreibetrag) → Keine Steuer`;
  if (zve <= cfg.zone2End) return `Zone 2: y = (ZVE−${n(cfg.gfb)})/10.000 → (${n(cfg.tarifZ2a, 2)}·y+${n(cfg.tarifZ2b)})·y`;
  if (zve <= cfg.zone3End) return `Zone 3: z = (ZVE−${n(cfg.zone2End)})/10.000 → (${n(cfg.tarifZ3a, 2)}·z+${n(cfg.tarifZ3b)})·z+${n(cfg.tarifZ3c, 2)}`;
  if (zve <= cfg.zone4End) return `Zone 4 (${pct(cfg.tarifZ4Satz)}): ${n(cfg.tarifZ4Satz, 2)}·ZVE − ${n(cfg.tarifZ4Abzug, 2)}`;
  if (cfg.zone5End == null || zve <= cfg.zone5End) return `Zone 5 (${pct(cfg.tarifZ5Satz)}): ${n(cfg.tarifZ5Satz, 2)}·ZVE − ${n(cfg.tarifZ5Abzug, 2)}`;
  return `Zone 6 (${pct(cfg.tarifZ6Satz)}): ${n(cfg.tarifZ6Satz, 2)}·ZVE − ${n(cfg.tarifZ6Abzug, 2)}`;
}

function LohnsteuerTooltipContent({ result, gh }) {
  const d = result.lstDetail;
  const cfg = getTaxConfig(gh.ghYear || new Date().getFullYear(), gh.ghMonth || (new Date().getMonth() + 1));
  const zveTarif = d.splittingActive ? Math.floor(d.ZVE / 2) : d.ZVE;
  const rows = [
    ['Jahresbrutto (JB)', fmtEuro(d.JB, 0)],
    ['− Arbeitnehmer-Pauschbetrag', fmtEuro(d.ANP, 0)],
    ['− Sonderausgaben-Pauschbetrag', fmtEuro(d.SAP || 36, 0)],
    ['− Vorsorgepauschale §39b', fmtEuro(d.sonderausgaben, 0)],
  ];
  const vspKvPv = gh.ghKvType === 'pkv' ? [
    ['PKV Basisanteil', fmtEuro((result.pkvBasis || result.kvAN + result.agZuschuss) * 12, 0)],
    ['− AG-Zuschuss (steuerfrei)', '−' + fmtEuro(result.agZuschuss * 12, 0)],
    ['= Ist-Beiträge KV/PV', fmtEuro(result.vspKVPVist, 0)],
  ] : [
    ['KV-AN', fmtEuro(result.kvAN * 12, 0)],
    ['+ PV-AN', fmtEuro(result.pvAN * 12, 0)],
    ['= Ist-Beiträge KV/PV', fmtEuro(result.vspKVPVist, 0)],
  ];

  return (
    <Box sx={{ p: 0.5, color: 'text.primary' }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.82rem', mb: 0.75 }}>
        Lohnsteuer-Berechnung §32a EStG
      </Typography>
      {rows.map(([l, v]) => (
        <Stack key={l} direction="row" justifyContent="space-between" spacing={1.5} sx={{ py: 0.25 }}>
          <Typography variant="caption">{l}</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{v}</Typography>
        </Stack>
      ))}

      {/* VSP §39b breakdown */}
      <Box sx={{
        bgcolor: 'action.hover', borderRadius: 1, p: 1, my: 0.5, fontSize: '0.68rem',
      }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
          Vorsorgepauschale §39b (Aufschlüsselung)
        </Typography>
        <Stack direction="row" justifyContent="space-between" sx={{ color: 'text.secondary' }}>
          <Typography variant="caption">1. RV-Anteil (9,3% AN, 100%)</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{fmtEuro(result.vspRV, 0)}</Typography>
        </Stack>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.secondary', mt: 0.5, mb: 0.25 }}>
          2. KV/PV-Teilbetrag (Günstigerprüfung):
        </Typography>
        {vspKvPv.map(([l, v]) => (
          <Stack key={l} direction="row" justifyContent="space-between" sx={{ color: 'text.secondary' }}>
            <Typography variant="caption">{l}</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{v}</Typography>
          </Stack>
        ))}
        <Stack direction="row" justifyContent="space-between" sx={{ color: 'text.secondary' }}>
          <Typography variant="caption">Mindest-VSP (12% Brutto)</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{fmtEuro(result.vspMindest, 0)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ color: 'text.secondary' }}>
          <Typography variant="caption">Deckel Stkl. {gh.ghStkl}</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{fmtEuro(result.vspDeckel, 0)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{
          color: result.vspGuenstiger === 'mindest' ? 'warning.main' : 'success.main',
          fontWeight: 600, mt: 0.25,
        }}>
          <Typography variant="caption" sx={{ fontWeight: 'inherit' }}>
            → Günstiger: {result.vspGuenstiger === 'mindest' ? 'Mindest-VSP' : 'Ist-Beiträge'}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 'inherit' }}>{fmtEuro(result.vspKVPV, 0)}</Typography>
        </Stack>
        {result.vspAV > 0 && (
          <>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.secondary', mt: 0.5, mb: 0.25 }}>
              3. AV-Anteil (§ 10 Abs. 1 Nr. 3a EStG):
            </Typography>
            <Stack direction="row" justifyContent="space-between" sx={{ color: 'text.secondary' }}>
              <Typography variant="caption">AV-AN (1,3%)</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{fmtEuro(result.vspAV, 0)}</Typography>
            </Stack>
          </>
        )}
        <Divider sx={{ my: 0.5 }} />
        <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 700 }}>
          <Typography variant="caption" sx={{ fontWeight: 'inherit' }}>= Gesamt Vorsorgepauschale</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 'inherit' }}>{fmtEuro(d.sonderausgaben, 0)}</Typography>
        </Stack>
      </Box>

      <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25, fontWeight: 600 }}>
        <Typography variant="caption" sx={{ fontWeight: 'inherit' }}>= ZVE</Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 'inherit' }}>{fmtEuro(d.ZVE, 0)}</Typography>
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}>
        Grundfreibetrag ({fmtEuro(d.GFB, 0)}) ist in Zone 1 der Tarifformel enthalten
      </Typography>

      <Divider sx={{ my: 0.75 }} />
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mb: 0.5 }}>
        {tarifZoneText(zveTarif, cfg)}
      </Typography>
      {d.splittingActive && (
        <Typography variant="caption" sx={{ display: 'block', color: 'info.main', mb: 0.5 }}>
          Splitting (Stkl. III): Ergebnis ÷ 2
        </Typography>
      )}
      <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 700 }}>
        <Typography variant="caption" sx={{ fontWeight: 'inherit' }}>= Lohnsteuer / Jahr</Typography>
        <Typography variant="caption" sx={{ color: 'error.main', fontFamily: 'monospace', fontWeight: 'inherit' }}>{fmtEuro(d.lstJahr, 0)}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 700 }}>
        <Typography variant="caption" sx={{ fontWeight: 'inherit' }}>= Lohnsteuer / Monat</Typography>
        <Typography variant="caption" sx={{ color: 'error.main', fontFamily: 'monospace', fontWeight: 'inherit' }}>{fmtEuro(d.lstJahr / 12, 2)}</Typography>
      </Stack>
    </Box>
  );
}

// ─── BMF Debug-Panel ──────────────────────────────────────────────────────────
// Stellt Lokal-Werte und BMF-`raw`-Felder nebeneinander, um Abweichungen
// präzise lokalisieren zu können (VSP, ZVE, Tarif, Soli).
function BmfDebugPanel({ local, bmf }) {
  const raw = bmf.raw || {};
  // BMF liefert alle Werte in CENTS — Umrechnung € (außer ZAHL-Felder wie STKL).
  const cents = (k) => (raw[k] != null ? raw[k] / 100 : null);
  const num   = (k) => (raw[k] != null ? raw[k]       : null);

  const fmt = (v, d = 2) =>
    v == null || isNaN(v) ? '—'
      : v.toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });
  const fmtE = (v, d = 0) => v == null ? '—' : fmt(v, d) + ' €';

  // Zentrale Vergleichszeilen: lokal vs BMF, Diff
  const d = local.lstDetail;
  const compareRows = [
    ['Jahresbrutto (RE4)',           d.JB,                cents('RE4') ?? d.JB],
    ['Sonderausgaben (VSP gesamt)',  local.sonderausgabenJahr, null /* BMF gibt VSP nicht direkt aus */],
    ['– davon RV-Anteil',            local.vspRV,         cents('VSP1') ?? cents('VSPRENT')],
    ['– davon KV/PV-Anteil',         local.vspKVPV,       cents('VSPN') ?? cents('VKVLZZ')],
    ['– davon AV-Anteil',            local.vspAV,         null],
    ['ZVE (zu vers. Einkommen)',     d.ZVE,               cents('ZVE')],
    ['LSt-Jahr',                     local.lstJahr,       cents('LSTJAHR') ?? cents('LSTLZZ')],
    ['Soli-Jahr',                    local.soliJahr,      cents('SOLZJ') ?? cents('SOLZLZZ')],
  ];

  // Alle BMF-`<ausgabe>`-Tags als Tabelle (rohe Werte in € umgerechnet, wo sinnvoll)
  const rawEntries = Object.entries(raw).sort(([a], [b]) => a.localeCompare(b));

  return (
    <Accordion disableGutters elevation={0} sx={{ mt: 1.5, '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          🔍 Detail-Vergleich (Lokal ↔ BMF)
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        {/* Curated Side-by-Side */}
        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.72rem', textTransform: 'uppercase' } }}>
              <TableCell component="th">Größe</TableCell>
              <TableCell component="th" align="right">Lokal</TableCell>
              <TableCell component="th" align="right">BMF</TableCell>
              <TableCell component="th" align="right">Diff</TableCell>
            </TableRow>
            {compareRows.map(([label, l, b]) => {
              const diff = (l != null && b != null) ? b - l : null;
              const hasDiff = diff != null && Math.abs(diff) >= 1;
              return (
                <TableRow key={label} sx={{ '& td': { fontSize: '0.78rem', fontFamily: 'monospace' } }}>
                  <TableCell sx={{ fontFamily: 'inherit !important', fontSize: '0.78rem !important' }}>{label}</TableCell>
                  <TableCell align="right">{fmtE(l)}</TableCell>
                  <TableCell align="right" sx={{ color: b == null ? 'text.disabled' : 'text.primary' }}>
                    {b == null ? '—' : fmtE(b)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: hasDiff ? 'warning.main' : 'text.disabled', fontWeight: hasDiff ? 700 : 400 }}>
                    {diff == null ? '—' : (diff > 0 ? '+' : '') + fmtE(diff)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {/* Roh-Dump aller BMF-Felder */}
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, bgcolor: 'action.hover', borderRadius: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              BMF-Rohdaten ({rawEntries.length} Felder)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 0.75, fontFamily: 'monospace', fontSize: '0.72rem' }}>
              {rawEntries.map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', py: 0.25 }}>
                  <Typography component="span" variant="caption" sx={{ fontFamily: 'inherit', color: 'text.secondary' }}>{k}</Typography>
                  <Typography component="span" variant="caption" sx={{ fontFamily: 'inherit' }}>
                    {v} {Number.isInteger(v) && Math.abs(v) > 100 ? `(${(v / 100).toLocaleString('de-DE')} €)` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </AccordionDetails>
    </Accordion>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function SalaryPage() {
  const theme = useTheme();
  const { settings, loading, saveSettings } = useSalarySettings();
  const { birthday: globalBirthday, isAdmin } = useModules();
  const age = calculateAge(globalBirthday);

  const [gh, setGh] = useState(DEFAULT_GEHALT);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [bmfStatus, setBmfStatus] = useState('idle');
  const [bmfResult, setBmfResult] = useState(null);
  const [activeTab, setActiveTab] = useState('current'); // 'current' | 'history'
  const debounceRef = useRef(null);
  const savedRef    = useRef(null);
  const mountedRef  = useRef(false);
  const hydratedRef = useRef(false);

  // Initial Hydration: settings → gh. Darf nur EINMAL feuern, sonst entsteht
  // eine Endlosschleife, weil saveSettings() intern wieder setSettings() aufruft,
  // was diesen Effect retriggern und gh mit einem neuen Objekt überschreiben würde.
  useEffect(() => {
    if (loading || hydratedRef.current) return;
    if (settings) setGh({ ...DEFAULT_GEHALT, ...settings });
    hydratedRef.current = true;
  }, [loading, settings]);

  useEffect(() => {
    if (!loading) {
      const id = setTimeout(() => { mountedRef.current = true; }, 100);
      return () => clearTimeout(id);
    }
  }, [loading]);

  const doSave = useCallback(async (params) => {
    setSaveStatus('saving');
    try {
      const netto = calcGehaltResult(params, params.ghPkvBeitrag, 0).netto;
      await saveSettings(params, netto);
      setSaveStatus('saved');
      clearTimeout(savedRef.current);
      savedRef.current = setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
    }
  }, [saveSettings]);

  useEffect(() => {
    if (!mountedRef.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSave(gh), 700);
    return () => clearTimeout(debounceRef.current);
  }, [gh, doSave]);

  function update(patch) {
    setGh((p) => ({ ...p, ...patch }));
    if (bmfStatus !== 'idle') { setBmfStatus('idle'); setBmfResult(null); }
  }

  const result = useMemo(() => calcGehaltResult(gh, gh.ghPkvBeitrag, 0), [gh]);
  const comp   = useMemo(() => calcNettoComparison(gh, gh.ghPkvBeitrag), [gh]);
  const cfg    = getTaxConfig(gh.ghYear || new Date().getFullYear(), gh.ghMonth || (new Date().getMonth() + 1));

  const mul  = gh.ghView === 'jahr' ? 12 : 1;
  const suf  = gh.ghView === 'jahr' ? ' / Jahr' : ' / Monat';
  const fmtE = (v) => fmtEuro(v * mul, 2);

  async function handleBmfValidate() {
    setBmfStatus('loading');
    setBmfResult(null);
    try {
      const r = await fetchBmfTaxValidation(gh, result);
      setBmfResult(r);
      if (!r.ok) setBmfStatus('error');
      else if (r.match) setBmfStatus('match');
      else setBmfStatus('mismatch');
    } catch (err) {
      setBmfStatus('error');
      setBmfResult({ ok: false, error: err.message });
    }
  }

  function syncFromPkvDraft() {
    try {
      const raw = localStorage.getItem('pkv_draft');
      if (!raw) return;
      const state = JSON.parse(raw);
      if (!state?.tarife?.length) return;
      const total = state.tarife.reduce((s, t) => s + (t.amount || 0), 0);
      const basis = state.tarife.filter(t => t.steuer).reduce((s, t) => s + (t.amount * (t.steuerPct || 0) / 100), 0);
      const agMax = calcAgZuschuss(total, gh.ghBrutto, gh.ghGkvZusatz);
      update({
        ghPkvBeitrag: Math.round(total * 100) / 100,
        ghPkvBasis: Math.round(basis * 100) / 100,
        ghPkvAgZuschuss: Math.round(agMax * 100) / 100,
        ghPkvSynced: true,
      });
    } catch {}
  }

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 200, color: 'text.secondary' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2">Lade Gehaltseinstellungen…</Typography>
        </Stack>
      </Stack>
    );
  }

  // ── Status indicator ────────────────────────────────────────────────────────
  let statusEl = null;
  if (saveStatus === 'saving') {
    statusEl = <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>↑ Speichert…</Typography>;
  } else if (saveStatus === 'saved') {
    statusEl = <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>✓ Gespeichert</Typography>;
  } else if (saveStatus === 'error') {
    statusEl = (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>✕ Fehler</Typography>
        <Button size="small" variant="outlined" color="error" onClick={() => doSave(gh)} startIcon={<RefreshIcon />}>
          Erneut versuchen
        </Button>
      </Stack>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100 }}>
      <PageHeader
        title="Gehaltsrechner" icon="calculate"
        subtitle="Netto-Berechnung mit Lohnsteuer, Sozialversicherung & PKV/GKV-Vergleich"
        actions={statusEl}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab value="current" label="Aktueller Rechner" />
          <Tab value="forecast" label="Forecast & Kaufkraft" />
          <Tab value="history" label="Gehaltshistorie" />
        </Tabs>
      </Box>

      {activeTab === 'forecast' && (
        <SalaryForecastTab baseParams={gh} />
      )}

      {activeTab === 'history' && (
        <SalaryHistoryTab baseParams={gh} />
      )}

      {activeTab === 'current' && (
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '300px 1fr' },
        gap: 3,
        alignItems: 'flex-start',
      }}>
        {/* ── Sidebar ── */}
        <Stack spacing={2}>
          <SectionCard title="Persönliche Daten">
            <Box>
              <Typography variant="caption" sx={{
                color: 'text.secondary', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                display: 'block', mb: 0.5,
              }}>
                Geburtsdatum
              </Typography>
              {globalBirthday ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    backgroundColor: 'background.default',
                    border: 1, borderColor: 'divider',
                    borderRadius: 1, px: 1.5, py: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {dayjs(globalBirthday).format('DD.MM.YYYY')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                    {age} Jahre
                  </Typography>
                </Stack>
              ) : (
                <Alert severity="warning" variant="outlined" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                  Bitte in den{' '}
                  <MuiLink href="/settings" sx={{ fontWeight: 700 }}>Einstellungen</MuiLink>
                  {' '}ergänzen.
                </Alert>
              )}
            </Box>
          </SectionCard>

          <SectionCard title="Gehalt">
            <Stack spacing={1.5}>
              <CurrencyField
                label="Bruttogehalt monatlich"
                value={gh.ghBrutto}
                onChange={(v) => update({ ghBrutto: v === '' ? 0 : v })}
                fullWidth
                inputProps={{ step: 100, min: 0 }}
              />
              <Stack direction="row" spacing={1.5}>
                <TextField
                  select label="Jahr" size="small" fullWidth
                  value={gh.ghYear ?? new Date().getFullYear()}
                  onChange={(e) => update({ ghYear: parseInt(e.target.value, 10) })}
                >
                  {AVAILABLE_YEARS.map((y) => <MenuItem key={y} value={y}>{getTaxConfig(y, 1).label}</MenuItem>)}
                </TextField>
                <TextField
                  select label="Monat" size="small" fullWidth
                  value={gh.ghMonth ?? (new Date().getMonth() + 1)}
                  onChange={(e) => update({ ghMonth: parseInt(e.target.value, 10) })}
                >
                  {MONATE.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>
              </Stack>
              {cfg.entwurf && (
                <Alert severity="warning" variant="outlined" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                  <AlertTitle sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                    {cfg.year} basiert auf Entwürfen (Stand {cfg.entwurf.stand})
                  </AlertTitle>
                  {cfg.entwurf.hinweis}
                </Alert>
              )}
              <CurrencyField
                label="Monatl. Freibetrag"
                value={gh.ghFreibetragMo}
                onChange={(v) => update({ ghFreibetragMo: v === '' ? 0 : v })}
                fullWidth
                inputProps={{ step: 10, min: 0 }}
                helperText="ELStAM-Freibetrag (§ 39a EStG) — reduziert das zu versteuernde Einkommen"
              />
              <TextField
                select label="Steuerklasse" value={gh.ghStkl}
                onChange={(e) => update({ ghStkl: parseInt(e.target.value, 10) })}
                fullWidth
              >
                {STEUERKLASSEN.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
              <TextField
                select label="Bundesland" value={gh.ghBundesland}
                onChange={(e) => update({ ghBundesland: parseInt(e.target.value, 10) })}
                fullWidth
              >
                {BUNDESLAENDER.map((b) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
              </TextField>
            </Stack>
          </SectionCard>

          <SectionCard title="Steuern & Abgaben">
            <Stack spacing={0.5}>
              {[
                ['Kirchensteuer', 'ghKist'],
                ['Rentenversicherung (9,3%)', 'ghRv'],
                ['Arbeitslosenversicherung (1,3%)', 'ghAv'],
              ].map(([label, key]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!gh[key]}
                      onChange={(e) => update({ [key]: e.target.checked })}
                    />
                  }
                  label={<Typography variant="body2">{label}</Typography>}
                />
              ))}
              <TextField
                select label="Kinder (PV-Staffelung)" value={gh.ghKinder}
                onChange={(e) => update({ ghKinder: parseInt(e.target.value, 10) })}
                fullWidth size="small" sx={{ mt: 1 }}
              >
                {[0, 1, 2, 3, 4, 5].map((k) => (
                  <MenuItem key={k} value={k}>
                    {k} {k === 0 ? 'Kinder (kinderlos +0,6%)' : k === 1 ? 'Kind' : `Kinder (−${(k - 1) * 0.25}% PV)`}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </SectionCard>

          <SectionCard title="Krankenversicherung">
            <ToggleButtonGroup
              value={gh.ghKvType}
              exclusive
              onChange={(_, v) => v && update({ ghKvType: v })}
              size="small"
              fullWidth
              sx={{ mb: 1.5 }}
            >
              <ToggleButton value="gkv">GKV</ToggleButton>
              <ToggleButton value="pkv">PKV</ToggleButton>
            </ToggleButtonGroup>

            {gh.ghKvType === 'gkv' ? (
              <CurrencyField
                label="GKV-Zusatzbeitrag"
                value={gh.ghGkvZusatz}
                onChange={(v) => update({ ghGkvZusatz: v === '' ? 1.7 : v })}
                adornment="%"
                decimals={2}
                inputProps={{ min: 0, max: 5, step: 0.01 }}
                fullWidth
                helperText={cfg.entwurf
                  ? `Satz deiner Kasse. Ø ${cfg.year}: ${cfg.kvZusatzDurchschnitt.toLocaleString('de-DE', { minimumFractionDigits: 1 })} %`
                  : undefined}
              />
            ) : (
              <Stack spacing={1.5}>
                <Button
                  variant="outlined" color="info" size="small"
                  startIcon={<LinkIcon />}
                  onClick={syncFromPkvDraft}
                >
                  Aus PKV-Rechner synchronisieren
                </Button>

                <CurrencyField
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>PKV-Beitrag Gesamt</span>
                      {gh.ghPkvSynced && <LinkIcon sx={{ fontSize: 12, color: 'info.main' }} />}
                    </Stack>
                  }
                  value={gh.ghPkvBeitrag}
                  onChange={(v) => update({ ghPkvBeitrag: v === '' ? 0 : v, ghPkvSynced: false })}
                  fullWidth
                  inputProps={{ step: 1, min: 0 }}
                />

                <CurrencyField
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>PKV-Basisanteil</span>
                      {gh.ghPkvSynced && <LinkIcon sx={{ fontSize: 12, color: 'info.main' }} />}
                    </Stack>
                  }
                  value={gh.ghPkvBasis || ''}
                  onChange={(v) => update({ ghPkvBasis: v === '' ? 0 : v, ghPkvSynced: false })}
                  fullWidth
                  placeholder="steuerlich absetzbar"
                  helperText="Nur Basisanteil mindert die Vorsorgepauschale (§39b EStG)"
                  inputProps={{ step: 1, min: 0 }}
                />

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>AG-Zuschuss</Typography>
                    <Button
                      size="small" variant="text"
                      onClick={() => {
                        const maxAg = calcAgZuschuss(gh.ghPkvBeitrag || 0, gh.ghBrutto, gh.ghGkvZusatz);
                        update({ ghPkvAgZuschuss: Math.round(maxAg * 100) / 100 });
                      }}
                      sx={{ minWidth: 0, fontSize: '0.6rem', py: 0 }}
                    >
                      Max. berechnen
                    </Button>
                  </Stack>
                  <CurrencyField
                    value={gh.ghPkvAgZuschuss || ''}
                    onChange={(v) => update({ ghPkvAgZuschuss: v === '' ? 0 : v, ghPkvSynced: false })}
                    fullWidth
                    placeholder="0"
                    inputProps={{ step: 1, min: 0 }}
                    helperText={cfg.rechengroessen
                      ? `Steuerfrei, max. ${fmtEuro(cfg.rechengroessen.agHoechstzuschussKvMitKrankengeld + cfg.rechengroessen.agHoechstzuschussPpv, 2)}/Mon (§257 SGB V, Entwurf)`
                      : 'Steuerfrei, max. 613,22 €/Mon (§257 SGB V)'}
                  />
                </Box>

                {gh.ghPkvBeitrag > 0 && (
                  <Alert severity="info" variant="outlined" icon={false} sx={{ fontSize: '0.72rem', py: 0.5 }}>
                    Eigenanteil: <strong>{fmtEuro(Math.max(0, (gh.ghPkvBeitrag || 0) - (gh.ghPkvAgZuschuss || 0)), 2)}/Mon</strong>
                    <br />Netto = Brutto − LSt − Soli − RV − AV − Eigenanteil
                  </Alert>
                )}
              </Stack>
            )}
          </SectionCard>
        </Stack>

        {/* ── Content ── */}
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          {/* View toggle */}
          <Stack direction="row" justifyContent="flex-end">
            <ToggleButtonGroup
              size="small"
              value={gh.ghView}
              exclusive
              onChange={(_, v) => v && update({ ghView: v })}
            >
              <ToggleButton value="monat">Monatlich</ToggleButton>
              <ToggleButton value="jahr">Jährlich</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Netto hero — Editorial Navy Block (Fiscal Gallery) */}
          <Paper sx={(t) => ({
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'primary.dark',
            color: 'primary.contrastText',
            borderRadius: 3,
            p: { xs: 4, sm: 5, md: 6 },
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 100%)`,
              opacity: 0.5,
              pointerEvents: 'none',
            },
          })}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="overline" sx={{ color: 'primary.light', display: 'block', mb: 2 }}>
                Nettoeinkommen{suf}
              </Typography>
              <Typography variant="h2" sx={{
                fontWeight: 900,
                letterSpacing: '-0.02em',
                lineHeight: 1,
              }}>
                {fmtE(result.netto)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'primary.light' }}>
                nach allen Abzügen · Steuerrecht {cfg.label}
              </Typography>
            </Box>
          </Paper>

          {/* BMF Validation — nur für Admin-Nutzer (Diagnose-Tool) */}
          {isAdmin && (
          <SectionCard
            title="BMF-Abgleich"
            subheader="Offizielle Lohnsteuer-Schnittstelle (LSt2026ext)"
            action={
              bmfStatus === 'idle' ? (
                <Button
                  variant="contained" size="small"
                  startIcon={<FactCheckOutlinedIcon />}
                  onClick={handleBmfValidate}
                >
                  Mit BMF abgleichen
                </Button>
              ) : bmfStatus === 'loading' ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">Prüfung läuft…</Typography>
                </Stack>
              ) : bmfStatus === 'match' && bmfResult ? (
                <MuiTooltip arrow placement="left" title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block' }}>Lokale LSt: <strong>{fmtEuro(bmfResult.localLstJahr, 0)}</strong>/Jahr</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>BMF LSt: <strong>{fmtEuro(bmfResult.bmfLstJahr, 0)}</strong>/Jahr</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>BMF Soli: {fmtEuro(bmfResult.bmfSoliJahr, 0)}/Jahr</Typography>
                  </Box>
                }>
                  <Chip label="✓ Amtlich geprüft" color="success" variant="outlined" size="small" sx={{ cursor: 'help' }} />
                </MuiTooltip>
              ) : bmfStatus === 'mismatch' && bmfResult ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    label={`⚠ Abweichung ${bmfResult.diff >= 0 ? '+' : ''}${fmtEuro(bmfResult.diff, 0)}/Jahr`}
                    color="warning" variant="outlined" size="small"
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                    Lokal: {fmtEuro(bmfResult.localLstJahr, 0)} · BMF: {fmtEuro(bmfResult.bmfLstJahr, 0)}
                  </Typography>
                  <IconButton size="small" onClick={handleBmfValidate}><RefreshIcon fontSize="inherit" /></IconButton>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={`✕ ${bmfResult?.error || 'BMF nicht erreichbar'}`} color="error" variant="outlined" size="small" />
                  <IconButton size="small" onClick={handleBmfValidate}><RefreshIcon fontSize="inherit" /></IconButton>
                </Stack>
              )
            }
          >
            {bmfStatus === 'idle' && (
              <Typography variant="caption" color="text.secondary">
                Lohnsteuer-Berechnung gegen die offizielle BMF-Schnittstelle prüfen.
              </Typography>
            )}
            {bmfResult?.ok && bmfResult?.raw && (
              <BmfDebugPanel local={result} bmf={bmfResult} />
            )}
          </SectionCard>
          )}

          {/* Abzüge table */}
          <SectionCard title="Abzüge im Überblick">
            {(() => {
              const isPkv         = gh.ghKvType === 'pkv';
              const pkvBrutto     = result.kvAN + result.agZuschuss;                 // voller PKV-Beitrag
              const steuerSumme   = result.lstMo + result.soliMo + result.kistMo;
              const sozialSumme   = isPkv
                ? result.rv + result.av                                              // PKV: nur gesetzliche SV
                : result.kvAN + result.pvAN + result.rv + result.av;
              const nettoOben     = isPkv
                ? result.brutto - steuerSumme - result.rv - result.av + result.agZuschuss
                : result.netto;
              const rows = isPkv ? [
                { label: 'Bruttogehalt',                     value: fmtE(result.brutto),      color: 'text.primary',   fw: 700 },
                { label: '− Lohnsteuer',                     value: fmtE(result.lstMo),       color: 'error.main',     tip: 'lst' },
                { label: '− Solidaritätsbeitrag',            value: fmtE(result.soliMo),      color: result.soliMo > 0.005 ? 'error.main' : 'text.secondary' },
                { label: '− Kirchensteuer',                  value: fmtE(result.kistMo),      color: result.kistMo > 0.005 ? 'error.main' : 'text.secondary' },
                { label: '− Rentenversicherung (AN)',        value: fmtE(result.rv),          color: result.rv > 0 ? 'warning.main' : 'text.secondary' },
                { label: '− Arbeitslosenversicherung (AN)',  value: fmtE(result.av),          color: result.av > 0 ? 'warning.main' : 'text.secondary' },
                { label: '+ AG-Zuschuss private KV + PV',    value: fmtE(result.agZuschuss),  color: 'success.main' },
              ] : [
                { label: 'Bruttogehalt',                     value: fmtE(result.brutto),      color: 'text.primary',   fw: 700 },
                { label: '− Lohnsteuer',                     value: fmtE(result.lstMo),       color: 'error.main',     tip: 'lst' },
                { label: '− Solidaritätsbeitrag',            value: fmtE(result.soliMo),      color: result.soliMo > 0.005 ? 'error.main' : 'text.secondary' },
                { label: '− Kirchensteuer',                  value: fmtE(result.kistMo),      color: result.kistMo > 0.005 ? 'error.main' : 'text.secondary' },
                { label: '− Krankenversicherung',            value: fmtE(result.kvAN),        color: 'warning.main' },
                { label: '− Pflegeversicherung',             value: fmtE(result.pvAN),        color: 'warning.main' },
                { label: '− Rentenversicherung (AN)',        value: fmtE(result.rv),          color: result.rv > 0 ? 'warning.main' : 'text.secondary' },
                { label: '− Arbeitslosenversicherung (AN)',  value: fmtE(result.av),          color: result.av > 0 ? 'warning.main' : 'text.secondary' },
              ];
              const rowEl = (r) => {
                const el = (
                  <Stack key={r.label} direction="row" justifyContent="space-between" alignItems="center"
                    sx={{ py: 0.75, cursor: r.tip ? 'help' : 'default' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: r.fw || 400 }}>{r.label}</Typography>
                    <Typography variant="body2" sx={{ color: r.color, fontWeight: r.fw || 600, fontFamily: 'monospace' }}>{r.value}</Typography>
                  </Stack>
                );
                if (r.tip !== 'lst') return el;
                return (
                  <MuiTooltip key={r.label} arrow placement="right"
                    slotProps={{ tooltip: { sx: { maxWidth: 380, bgcolor: 'background.paper', color: 'text.primary', border: 1, borderColor: 'divider', boxShadow: 4, p: 1.5 } } }}
                    title={<LohnsteuerTooltipContent result={result} gh={gh} />}>
                    {el}
                  </MuiTooltip>
                );
              };
              const subtotalEl = (label, val, color = 'success.main') => (
                <Stack direction="row" justifyContent="space-between"
                  sx={{ py: 1, my: 0.5, borderTop: 2, borderBottom: 2, borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
                  <Typography variant="body1" sx={{ color, fontWeight: 700, fontFamily: 'monospace' }}>{val}</Typography>
                </Stack>
              );

              return (
                <>
                  <Stack divider={<Divider flexItem />} spacing={0}>
                    {rows.map(rowEl)}
                  </Stack>

                  {/* Nettogehalt */}
                  {subtotalEl('= Nettogehalt', fmtE(nettoOben))}

                  {/* Verbleibend-Block nur bei PKV */}
                  {isPkv && (
                    <>
                      <Stack divider={<Divider flexItem />} spacing={0}>
                        {rowEl({ label: '− private Krankenversicherung', value: fmtE(pkvBrutto), color: 'error.main' })}
                      </Stack>
                      {subtotalEl('= Verbleibend', fmtE(result.netto))}
                    </>
                  )}

                  {/* Summary: Steuer + Sozial */}
                  <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Steuerabzug gesamt</Typography>
                      <Typography variant="caption" sx={{ color: 'error.main', fontFamily: 'monospace', fontWeight: 600 }}>
                        − {fmtE(steuerSumme)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Sozialabgaben gesamt</Typography>
                      <Typography variant="caption" sx={{ color: 'warning.main', fontFamily: 'monospace', fontWeight: 600 }}>
                        − {fmtE(sozialSumme)}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>
                    Vorsorgepauschale §39b EStG: {fmtEuro(result.sonderausgabenJahr / 12, 2)}/Monat
                    ({fmtEuro(result.sonderausgabenJahr, 0)}/Jahr) mindert Lohnsteuer
                  </Typography>
                </>
              );
            })()}
          </SectionCard>

          {/* Arbeitgeberkosten (AG-Anteile SV) */}
          <SectionCard title="Arbeitgeberkosten" subheader="AG-Anteile zur Sozialversicherung, gedeckelt an den BBG">
            <Stack divider={<Divider flexItem />} spacing={0}>
              {[
                { label: 'Bruttogehalt',                         value: result.brutto, fw: 700 },
                { label: '+ Rentenversicherung (AG)',            value: result.agRv },
                { label: '+ Arbeitslosenversicherung (AG)',      value: result.agAv },
                ...(gh.ghKvType === 'pkv'
                  ? [{ label: '+ AG-Zuschuss private KV + PV',   value: result.agKv }]
                  : [{ label: '+ Krankenversicherung (AG)',      value: result.agKv },
                     { label: '+ Pflegeversicherung (AG)',       value: result.agPv }]),
              ].map((r) => (
                <Stack key={r.label} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: r.fw || 400 }}>{r.label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: r.fw || 600, fontFamily: 'monospace' }}>{fmtE(r.value)}</Typography>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" justifyContent="space-between"
              sx={{ py: 1, my: 0.5, borderTop: 2, borderBottom: 2, borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>= Gesamtkosten Arbeitgeber</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{fmtE(result.arbeitgeberkosten)}</Typography>
            </Stack>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>
              Lohnnebenkosten {fmtE(result.agGesamt)}{suf} · ohne Umlagen U1/U2/U3 und Unfallversicherung
            </Typography>
          </SectionCard>

          {/* PKV vs GKV netto comparison */}
          {(gh.ghKvType === 'pkv' && gh.ghPkvBeitrag > 0) && (
            <SectionCard title="PKV vs. GKV · Netto-Vergleich">
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 2 }}>
                <Paper sx={{ bgcolor: 'surface.low', borderRadius: 3, p: 2.5 }}>
                  <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                    Netto mit PKV (AN)
                  </Typography>
                  <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 800 }}>
                    {fmtEuro(comp.nettoPkv, 2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    PKV-AN {fmtEuro(comp.kvPkv, 2)} · AG {fmtEuro(comp.agZ, 2)}
                  </Typography>
                </Paper>
                <Paper sx={{ bgcolor: 'surface.low', borderRadius: 3, p: 2.5 }}>
                  <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                    Netto mit GKV (AN)
                  </Typography>
                  <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 800 }}>
                    {fmtEuro(comp.nettoGkv, 2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    KV {fmtEuro(comp.kvGkv, 2)} + PV {fmtEuro(comp.pvGkv, 2)}
                  </Typography>
                </Paper>
              </Box>
              <Alert
                severity={Math.abs(comp.nettoDiff) < 1 ? 'info' : comp.nettoDiff > 0 ? 'success' : 'error'}
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              >
                {Math.abs(comp.nettoDiff) < 1
                  ? '≈ Gleichauf'
                  : comp.nettoDiff > 0
                    ? `PKV → +${fmtEuro(Math.abs(comp.nettoDiff), 2)}/Monat mehr Netto`
                    : `GKV → +${fmtEuro(Math.abs(comp.nettoDiff), 2)}/Monat mehr Netto`}
              </Alert>
            </SectionCard>
          )}

          {/* Hint: budget integration */}
          <Alert severity="success" variant="outlined">
            Netto wird automatisch gespeichert und als Einnahmequelle im Budget-Modul vorgeschlagen.
          </Alert>
        </Stack>
      </Box>
      )}
    </Box>
  );
}
